import React, { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import moment from "moment";
import { RootState } from "reducers/rootReducer";
import { SocketTopics } from "constants/enums";
import { BeamCamera, BeamCameraAlert, BeamDetection } from "./types";
import { latestRxSample, toDetections } from "./adapters";
import { DEMO_TICK_MS, demoDetections } from "./demoData";
import BeamIcon from "./BeamIcon";
import "./MessageTypes.scss";

/**
 * The Video Display: one camera, opened from the grid.
 *
 * Deliberately *not* an operations console. The page's chat already carries
 * every alert this camera raises, in the same column it always has, so
 * repeating that list beside the video would be the same information twice —
 * and the second copy would be the one without the replies, reactions and
 * context the chat gives it. What survives here is the one alert that explains
 * why this feed was opened.
 */

interface CameraVideoDisplayProps {
  camera: BeamCamera;
  /** Alerts already matched to this camera, newest first. */
  alerts: BeamCameraAlert[];
  pageId?: string | number;
  socket: ISocket;
  /** This camera is a stand-in, so its feed and its inference are drawn here. */
  demo?: boolean;
  onBack: () => void;
}

type StreamState =
  | "idle"
  | "starting"
  | "live"
  | "unavailable"
  | "error"
  | "demo";

const SEVERITY_COLORS: Record<string, string> = {
  critical: "#FF5F25",
  warning: "#FBAE16",
  info: "#508afa",
};

/**
 * Detection colour follows the same reading as the alert accent: a class that
 * triggered the rule is the one the eye should land on first.
 */
const detectionColor = (label: string, hasAlert: boolean): string => {
  if (!hasAlert) return "#2ECC71";
  return label === "People" ? "#FF5F25" : "#FBAE16";
};

const CameraVideoDisplay: FC<CameraVideoDisplayProps> = ({
  camera,
  alerts,
  pageId,
  socket,
  demo,
  onBack,
}) => {
  const { rx } = useSelector((state: RootState) => state.dashboard);

  const [overlay, setOverlay] = useState(true);
  const [stream, setStream] = useState<StreamState>("idle");
  const [streamError, setStreamError] = useState<string>("");
  /** Kept per class so a count that stops being reported does not vanish. */
  const [detections, setDetections] = useState<BeamDetection[]>([]);
  const [lastSampleAt, setLastSampleAt] = useState<number>(0);

  const activeAlert = alerts[0];

  /**
   * beamAI inference for this camera arrives on its own topic, so the
   * subscription is scoped to the open camera rather than to every tile in the
   * grid — eight idle subscriptions would cost eight streams of samples nobody
   * is looking at.
   */
  useEffect(() => {
    if (demo || !camera.iotTopic) return;
    socket.subscribeToEvent(SocketTopics.SubscribePageTopics, camera.iotTopic);
    return () => {
      socket.unSubscribeToEvent(
        SocketTopics.UnSubscribePageTopics,
        camera.iotTopic
      );
    };
  }, [camera.iotTopic]);

  /**
   * A stand-in camera has no inference topic behind it, so its detections are
   * generated on the same cadence a device would publish on — the overlay is
   * what this screen is for, and a static frame would review none of it.
   */
  useEffect(() => {
    if (!demo) return;
    let tick = 0;
    const publish = () => {
      setDetections(demoDetections(camera.id, tick));
      setLastSampleAt(Date.now());
      tick += 1;
    };
    publish();
    const handle = setInterval(publish, DEMO_TICK_MS);
    return () => clearInterval(handle);
  }, [demo, camera.id]);

  /** Samples replace the previous reading for their class and add new ones. */
  useEffect(() => {
    if (demo) return;
    const incoming = toDetections(rx);
    if (!incoming.length) return;
    setDetections((previous) => {
      const merged = [...previous];
      incoming.forEach((detection) => {
        const index = merged.findIndex((item) => item.label === detection.label);
        if (index === -1) merged.push(detection);
        else merged[index] = detection;
      });
      return merged;
    });
    setLastSampleAt(latestRxSample(rx)?.timestamp || Date.now());
  }, [rx]);

  /**
   * The browser cannot play RTSP, so the video service transcodes it. The same
   * stop-then-start pair the stream item already uses is reproduced here: stop
   * clears a session left behind by a previous viewer, and is expected to fail
   * when there is none — only the start result decides whether we have a feed.
   */
  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      if (demo) {
        setStream("demo");
        return;
      }
      if (!camera.rtspUrl || !pageId) {
        setStream("unavailable");
        return;
      }
      setStream("starting");
      const name = camera.streamName || camera.id;
      const base = process.env.REACT_APP_VIDEO_URI;
      try {
        await fetch(
          `${base}/api/rtsp/stop?streamName=${encodeURIComponent(
            name
          )}&roomName=${encodeURIComponent(String(pageId))}`
        ).catch(() => undefined);

        const response = await fetch(
          `${base}/api/rtsp/start?rtspUrl=${encodeURIComponent(
            camera.rtspUrl
          )}&roomName=${encodeURIComponent(
            String(pageId)
          )}&streamName=${encodeURIComponent(name)}`
        );
        if (cancelled) return;
        if (!response.ok) throw new Error(response.statusText);
        setStream("live");
      } catch (error: any) {
        if (cancelled) return;
        setStreamError(error?.message || "Stream could not be started");
        setStream("error");
      }
    };

    start();
    return () => {
      cancelled = true;
    };
  }, [demo, camera.id, camera.rtspUrl, pageId]);

  const viewerUrl = useMemo(
    () =>
      pageId
        ? `${process.env.REACT_APP_VIDEO_URI}/?pageId=${encodeURIComponent(
            String(pageId)
          )}&viewer=true`
        : "",
    [pageId]
  );

  const boxed = detections.filter((detection) => detection.box);

  return (
    <section className="mt-card mt-camera-display">
      <header className="mt-card__head mt-camera-display__head">
        <button type="button" className="mt-btn mt-btn--link" onClick={onBack}>
          <LeftOutlined /> All cameras
        </button>
        <div className="mt-camera-display__title">
          <h4>
            {camera.name}
            {demo && <span className="mt-demo-badge">Demo</span>}
          </h4>
          {camera.location && <span>{camera.location}</span>}
        </div>
        <button
          type="button"
          className={`mt-toggle ${overlay ? "is-on" : ""}`}
          onClick={() => setOverlay(!overlay)}
        >
          beamAI overlay {overlay ? "on" : "off"}
        </button>
      </header>

      {/*
        The flag that brought the operator here, kept as a single banner. The
        full history of this camera's alerts is in the page chat.
      */}
      {activeAlert && (
        <div
          className="mt-camera-display__flag"
          style={{ borderColor: SEVERITY_COLORS[activeAlert.severity] }}
        >
          <BeamIcon name="flag" size={16} />
          <div>
            <strong>{activeAlert.type}</strong>
            {activeAlert.description && <p>{activeAlert.description}</p>}
          </div>
          <Tooltip
            title={moment(activeAlert.occurredAt).format("D MMM YYYY, h:mm:ss A")}
          >
            <span>{moment(activeAlert.occurredAt).fromNow()}</span>
          </Tooltip>
        </div>
      )}

      <div className="mt-camera-display__stage">
        {stream === "live" && viewerUrl ? (
          <iframe
            title={`${camera.name} live feed`}
            src={viewerUrl}
            className="mt-camera-display__video"
            allow="autoplay; fullscreen; picture-in-picture"
          />
        ) : stream === "demo" ? (
          <img
            className="mt-camera-display__video"
            src={camera.snapshotUrl}
            alt={`${camera.name} sample frame`}
          />
        ) : (
          <div className="mt-camera-display__placeholder">
            {stream === "starting" && <span>Negotiating live stream…</span>}
            {stream === "unavailable" && (
              <span>
                No RTSP source is configured for this camera. beamAI detections
                below are live.
              </span>
            )}
            {stream === "error" && (
              <span>Live stream unavailable — {streamError}</span>
            )}
          </div>
        )}

        {/*
          Boxes are drawn only where the sample carried coordinates. The counts
          the inference service publishes today have no geometry, so the strip
          under the stage is the honest rendering of them — a rectangle placed
          at a guessed position would read as a real detection.
        */}
        {overlay &&
          boxed.map((detection, index) => {
            const [x, y, width, height] = detection.box;
            const color = detectionColor(detection.label, Boolean(activeAlert));
            return (
              <div
                key={`${detection.label}-${index}`}
                className="mt-camera-display__box"
                style={{
                  left: `${x * 100}%`,
                  top: `${y * 100}%`,
                  width: `${width * 100}%`,
                  height: `${height * 100}%`,
                  borderColor: color,
                }}
              >
                <span style={{ background: color }}>
                  {detection.label}
                  {detection.confidence !== undefined &&
                    ` ${(detection.confidence * 100).toFixed(1)}%`}
                </span>
              </div>
            );
          })}

        <span className="mt-camera-display__live">
          <i />{" "}
          {stream === "live" ? "LIVE" : stream === "demo" ? "DEMO" : "STANDBY"}
        </span>
      </div>

      {overlay && (
        <div className="mt-camera-display__detections">
          <div className="mt-camera-display__detections-head">
            <span>beamAI detections</span>
            {lastSampleAt > 0 && (
              <span>updated {moment(lastSampleAt).fromNow()}</span>
            )}
          </div>
          {detections.length ? (
            <div className="mt-chip-row">
              {detections.map((detection) => (
                <span
                  key={detection.label}
                  className="mt-chip"
                  style={{
                    borderColor: detectionColor(
                      detection.label,
                      Boolean(activeAlert)
                    ),
                  }}
                >
                  {detection.label}
                  <b>{detection.count}</b>
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-empty mt-empty--inline">
              {demo
                ? "Nothing is in frame on this sample camera right now."
                : camera.iotTopic
                ? "Waiting for the first inference sample from this camera."
                : "This camera publishes no beamAI topic, so no detections arrive for it."}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default CameraVideoDisplay;
